import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AdminPracticeQuestion } from "@/lib/admin-api";
import AdminTable from "@/components/admin/AdminTable";
import DeleteQuestionButton from "@/components/admin/DeleteQuestionButton";

/**
 * apps/web :: components/admin :: QuestionListTable
 *
 * Server-paginated table of admin practice questions, rendered through
 * the shared {@link AdminTable} (ROADS-478 phase 5). AdminTable is
 * configured with `enablePagination={false}` because pagination is
 * driven by the backend offset/limit pair; the existing prev/next
 * chrome lives below AdminTable as a sibling and calls back through
 * `onPageChange`.
 *
 * Display rules (carried over from the pre-AdminTable version):
 *   - id column shows the first 8 chars of the UUID; the full id only
 *     surfaces in the edit URL.
 *   - question text truncates at 80 chars with an ellipsis.
 *   - Per-row Delete uses the two-step confirm — single-click destruction
 *     is impossible by design (Wave 6b Hard Rule #7).
 */
const MAX_Q_TEXT = 80;

export interface QuestionListTableProps {
  questions: AdminPracticeQuestion[];
  totalCount: number;
  limit: number;
  offset: number;
  onPageChange: (nextOffset: number) => void;
  onDelete: (id: string) => void;
  deletingId?: string | null;
}

function truncate(value: string, n: number): string {
  return value.length <= n ? value : `${value.slice(0, n - 1)}…`;
}

function formatCreated(ms: number): string {
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return "—";
  }
}

export default function QuestionListTable({
  questions,
  totalCount,
  limit,
  offset,
  onPageChange,
  onDelete,
  deletingId,
}: QuestionListTableProps) {
  const { t } = useTranslation();
  const start = totalCount === 0 ? 0 : offset + 1;
  const end = Math.min(offset + questions.length, totalCount);
  const canPrev = offset > 0;
  const canNext = offset + limit < totalCount;

  const columns = useMemo<ColumnDef<AdminPracticeQuestion, unknown>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: t("admin.list.columns.id"),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.id.slice(0, 8)}</span>
        ),
      },
      {
        id: "state_code",
        accessorKey: "state_code",
        header: t("admin.list.columns.state"),
      },
      {
        id: "locale",
        accessorKey: "locale",
        header: t("admin.list.columns.locale"),
      },
      {
        id: "category",
        accessorKey: "category",
        header: t("admin.list.columns.category"),
        cell: ({ row }) =>
          t(`admin.category.${row.original.category.toLowerCase()}`, {
            defaultValue: row.original.category.replaceAll("_", " "),
          }),
      },
      {
        id: "difficulty",
        accessorKey: "difficulty",
        header: t("admin.list.columns.difficulty"),
      },
      {
        id: "question_text",
        accessorKey: "question_text",
        header: t("admin.list.columns.question"),
        cell: ({ row }) => truncate(row.original.question_text, MAX_Q_TEXT),
      },
      {
        id: "created_at_ms",
        accessorFn: (row) => row.created_at_ms,
        header: t("admin.list.columns.created"),
        sortDescFirst: false,
        cell: ({ row }) => (
          <span className="text-xs text-neutral-600">
            {formatCreated(row.original.created_at_ms)}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("admin.list.columns.actions"),
        enableSorting: false,
        cell: ({ row }) => {
          const q = row.original;
          return (
            <div className="flex items-center gap-xs">
              <Link
                to={`/admin/questions/${encodeURIComponent(q.id)}`}
                data-testid={`admin-question-edit-${q.id}`}
                className="inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-sm py-xs text-xs font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("admin.list.actions.edit")}
              </Link>
              <DeleteQuestionButton
                onConfirm={() => onDelete(q.id)}
                pending={deletingId === q.id}
                label={t("admin.list.actions.delete")}
              />
            </div>
          );
        },
      },
    ],
    [t, onDelete, deletingId],
  );

  if (questions.length === 0) {
    return (
      <div
        data-testid="admin-list-empty"
        role="status"
        className="rounded-md border border-neutral-200 bg-white p-lg text-center text-sm text-neutral-700"
      >
        {t("admin.list.empty")}
      </div>
    );
  }

  return (
    <div>
      <AdminTable<AdminPracticeQuestion>
        data={questions}
        columns={columns}
        enablePagination={false}
        testId="admin-question-table"
        rowTestId={(q) => `admin-question-row-${q.id}`}
      />

      <div className="mt-md flex items-center justify-between text-sm">
        <span data-testid="admin-list-range" className="text-neutral-700">
          {t("admin.list.pagination.range", { start, end, total: totalCount })}
        </span>
        <div className="flex gap-sm">
          <button
            type="button"
            data-testid="admin-list-prev"
            disabled={!canPrev}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
            className="inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-sm py-xs text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("admin.list.pagination.prev")}
          </button>
          <button
            type="button"
            data-testid="admin-list-next"
            disabled={!canNext}
            onClick={() => onPageChange(offset + limit)}
            className="inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-sm py-xs text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("admin.list.pagination.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
